import { useRef, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectCurrentToken } from "../auth/authSlice";
import { uploadFileToS3Presigned, reportAvUploadFailure } from "../../utils/presignedUpload";
import { useUploadWebcamCaptureMutation } from "./chatSessionApiSlice";

const CAPTURE_INTERVAL_MS = 60_000;
const JPEG_QUALITY = 0.92;

/**
 * Hook to upload webcam snapshots to S3 at 1-minute intervals during a session.
 *
 * @param {{ sessionId: string }} options
 * @returns {{
 *   videoRef: React.RefObject,
 *   consentGiven: boolean,
 *   isCapturing: boolean,
 *   cameraError: string|null,
 *   uploadFailed: boolean,
 *   handleConsent: () => void,
 *   startCapture: () => void,
 *   stopCapture: () => void,
 * }}
 */
export function useWebcamCapture({ sessionId }) {
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const intervalRef = useRef(null);
  const canvasRef = useRef(null);
  const captureRequestedRef = useRef(false);
  const reportedFailureRef = useRef(false);

  const [consentGiven, setConsentGiven] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [uploadFailed, setUploadFailed] = useState(false);

  const token = useSelector(selectCurrentToken);
  const [uploadWebcamCapture] = useUploadWebcamCaptureMutation();

  // Called when consent is given (optimistic update)
  const handleConsent = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, facingMode: "user" },
      });
      cameraStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setConsentGiven(true); // Trigger useEffect once camera is ready
    } catch (err) {
      console.error("Camera permission denied:", err);
      setConsentGiven(true); // Close modal but show error
      setCameraError("카메라 접근 권한이 필요합니다. 브라우저 설정에서 허용해주세요.");
    }
  }, []);

  // Captures and uploads a single snapshot
  const captureAndUpload = useCallback(async () => {
    if (!videoRef.current || !cameraStreamRef.current) return;

    // Use actual video dimensions for native resolution capture
    const videoWidth = videoRef.current.videoWidth;
    const videoHeight = videoRef.current.videoHeight;
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    canvasRef.current.width = videoWidth;
    canvasRef.current.height = videoHeight;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) {
      console.error("Unable to obtain 2D canvas context for webcam capture");
      return;
    }
    ctx.drawImage(videoRef.current, 0, 0, videoWidth, videoHeight);

    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return;

      const timestamp = new Date().toISOString();
      const filename = `capture-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: "image/jpeg" });

      // Request presigned URL and upload to S3
      const { key, success, error } = await uploadFileToS3Presigned(file, token, `av/${sessionId}/webcam/`);
      if (!success) {
        console.error("Webcam snapshot S3 upload failed:", error);
        // Retried on the next interval; reported once so a broken bucket is noticed.
        if (!reportedFailureRef.current) {
          reportedFailureRef.current = true;
          reportAvUploadFailure(sessionId, token, "webcam", error);
        }
        setUploadFailed(true);
        return;
      }

      // RTK mutations resolve with { error } rather than throwing.
      const res = await uploadWebcamCapture({ sessionId, s3_key: key, timestamp });
      if (res.error) console.error("Webcam capture registration failed:", res.error);
    }, "image/jpeg", JPEG_QUALITY);
  }, [sessionId, token, uploadWebcamCapture]);

  const startCapture = useCallback(() => {
    if (intervalRef.current) return;
    captureRequestedRef.current = true;
    if (!cameraStreamRef.current) return;
    setIsCapturing(true);

    captureAndUpload(); // when Session starts, capture once

    intervalRef.current = setInterval(captureAndUpload, CAPTURE_INTERVAL_MS);
  }, [captureAndUpload]);

  const stopCapture = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsCapturing(false);
  }, []);

  // Start interval if capture was requested and camera stream is ready
  useEffect(() => {
    if (cameraStreamRef.current && captureRequestedRef.current && !intervalRef.current) {
      setIsCapturing(true);
      intervalRef.current = setInterval(captureAndUpload, CAPTURE_INTERVAL_MS);
    }
  }, [consentGiven, captureAndUpload]);

  useEffect(() => {
    return () => {
      stopCapture();
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, [stopCapture]);

  return {
    videoRef,
    consentGiven,
    isCapturing,
    cameraError,
    uploadFailed,
    handleConsent,
    startCapture,
    stopCapture,
  };
}
